/**
 * Почему браузер не дал микрофон — точная причина в консоль и в PostHog.
 *
 * Самые частые причины на телефоне:
 *   insecure      — страница не по HTTPS (например, http://192.168.1.5:5173):
 *                   браузер прячет `navigator.mediaDevices` целиком;
 *   unsupported   — встроенный браузер приложения (Telegram, Instagram…)
 *                   или старый WebView без `getUserMedia`;
 *   denied        — `NotAllowedError` / `SecurityError`: запрет в настройках
 *                   сайта или телефона (или запрос вне жеста на iOS);
 *   not-found     — `NotFoundError`: микрофона нет;
 *   busy          — `NotReadableError` / `AbortError`: микрофон занят
 *                   (звонок, диктофон, другая вкладка);
 *   speech-service — системное распознавание выключено (iOS: диктовка).
 */

import { audioSessionType } from '../lib/audioSession';
import { isIOS } from '../lib/platform';
import { track } from './posthogService';

export type MicFailureKind = 'insecure' | 'unsupported' | 'denied' | 'not-found' | 'busy' | 'speech-service' | 'other';

export interface MicFailure {
  readonly kind: MicFailureKind;
  /** Имя ошибки браузера (`NotAllowedError`…) или причина распознавания (`not-allowed`…). */
  readonly name: string;
  readonly message: string;
  /** Где случилось: `prime` (касание), `stream`, `record`, `listen`, `check`. */
  readonly stage: string;
  readonly at: number;
}

export type MicPlatform = 'ios' | 'android' | 'desktop';

export function micPlatform(): MicPlatform {
  if (isIOS()) return 'ios';
  if (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)) return 'android';
  return 'desktop';
}

/** Встроенный браузер приложения: микрофон там часто закрыт самим приложением. */
function inAppBrowser(): string | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  const match = /(Telegram|Instagram|FBAN|FBAV|FB_IAB|VKClient|Line\/|MicroMessenger|WhatsApp|YaBrowser\/[\d.]+ \(lite\))/i.exec(ua);
  return match?.[1] ?? null;
}

/** Окружение страницы — всё, что влияет на доступ к микрофону. */
export function micEnvironment() {
  const hasWindow = typeof window !== 'undefined';
  const speechWindow = (hasWindow ? window : {}) as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown; AudioContext?: unknown; webkitAudioContext?: unknown; MediaRecorder?: unknown };
  return {
    secureContext: hasWindow ? window.isSecureContext : false,
    protocol: hasWindow ? window.location.protocol : '',
    host: hasWindow ? window.location.host : '',
    platform: micPlatform(),
    standalone: hasWindow && (window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as { standalone?: boolean }).standalone === true),
    inApp: inAppBrowser(),
    mediaDevices: typeof navigator !== 'undefined' && navigator.mediaDevices !== undefined,
    getUserMedia: typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function',
    permissionsApi: typeof navigator !== 'undefined' && typeof navigator.permissions?.query === 'function',
    speechRecognition: speechWindow.SpeechRecognition !== undefined || speechWindow.webkitSpeechRecognition !== undefined,
    mediaRecorder: speechWindow.MediaRecorder !== undefined,
    audioContext: speechWindow.AudioContext !== undefined || speechWindow.webkitAudioContext !== undefined,
    /** Тип аудиосессии Safari: `playback` несовместим с записью (InvalidStateError). */
    audioSession: audioSessionType(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

/** Что мешает микрофону ещё до попытки; `null` — пробовать можно. */
export function micBlockerBeforeTry(): MicFailureKind | null {
  if (typeof window === 'undefined') return 'unsupported';
  if (!window.isSecureContext) return 'insecure';
  if (typeof navigator.mediaDevices?.getUserMedia !== 'function') return 'unsupported';
  return null;
}

function kindOfName(name: string): MicFailureKind {
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
    case 'not-allowed':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'not-found';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
    case 'InvalidStateError': // Safari: аудиосессия «воспроизведение» — микрофон занят звуком
    case 'audio-capture':
      return 'busy';
    case 'service-not-allowed':
      return 'speech-service';
    case 'insecure':
      return 'insecure';
    case 'unsupported':
    case 'TypeError':
      return 'unsupported';
    default:
      return 'other';
  }
}

let lastFailure: MicFailure | null = null;

/** Последний сбой микрофона, если он свежий: экран восстановления показывает его причину. */
export function recentMicFailure(withinMs = 8000): MicFailure | null {
  return lastFailure !== null && Date.now() - lastFailure.at <= withinMs ? lastFailure : null;
}

/**
 * Записать сбой: ошибка браузера (`DOMException`), причина распознавания
 * (`'not-allowed'`) или заранее известная помеха (`'insecure'`).
 */
export function reportMicFailure(stage: string, error: unknown): MicFailure {
  let name: string;
  let message: string;
  if (typeof error === 'string') {
    name = error;
    message = error === 'insecure' ? `page is not a secure context (${typeof window === 'undefined' ? '' : window.location.origin})` : error;
  } else if (error instanceof Error || (typeof error === 'object' && error !== null && 'name' in error)) {
    name = String((error as { name?: unknown }).name ?? 'Error');
    message = String((error as { message?: unknown }).message ?? '');
  } else {
    name = 'Unknown';
    message = String(error);
  }
  // Небезопасная страница даёт TypeError/undefined — причина на самом деле в HTTP.
  const environment = micEnvironment();
  const kind: MicFailureKind = !environment.secureContext ? 'insecure' : kindOfName(name);
  lastFailure = { kind, name, message, stage, at: Date.now() };
  console.error(`[mic] ${stage}: ${name}: ${message}`, { kind, ...environment });
  track('mic_error', {
    stage,
    kind,
    name,
    message: message.slice(0, 300),
    secureContext: environment.secureContext,
    protocol: environment.protocol,
    platform: environment.platform,
    standalone: environment.standalone,
    inApp: environment.inApp ?? '',
    speechRecognition: environment.speechRecognition,
    mediaRecorder: environment.mediaRecorder,
    audioSession: environment.audioSession ?? '',
  });
  return lastFailure;
}

/** Поток оборвался во время звонка: почему и на какой минуте. */
export function reportMicDropped(reason: string, heldMs: number, extra: { readonly contextState?: string } = {}): void {
  const environment = micEnvironment();
  // Последняя точная ошибка браузера (NotAllowedError, NotReadableError, InvalidStateError…), если была.
  const failure = recentMicFailure(30_000);
  const errorName = failure === null ? '' : failure.name;
  console.warn(`[mic] dropped during call: ${reason} after ${Math.round(heldMs / 1000)} s`, {
    error: failure === null ? null : `${failure.name}: ${failure.message}`,
    audioContext: extra.contextState ?? null,
    audioSession: environment.audioSession,
    standalone: environment.standalone,
  });
  track('mic_dropped', {
    reason,
    heldSec: Math.round(heldMs / 1000),
    platform: environment.platform,
    standalone: environment.standalone,
    audioSession: environment.audioSession ?? '',
    errorName,
    contextState: extra.contextState ?? '',
  });
}

/** Микрофон снова работает после сбоя — видно, помогает ли «Попробовать снова». */
export function reportMicRecovered(stage: string, attempts: number): void {
  console.info(`[mic] recovered after ${attempts} attempt(s) at ${stage}`);
  track('mic_recovered', { stage, attempts, platform: micPlatform() });
}
